import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { ClientFormData } from '../../services/api'

interface Client {
  id: string
  name: string
  slug: string
  linkedTenantId: string | null
  linkedTenant: { id: string; name: string; slug: string } | null
  status: 'active' | 'inactive' | 'prospect'
  clientType: 'professional' | 'hybrid'
  ownerUserId?: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  legalName: string | null
  taxId: string | null
  billingEmails: string[]
  address: string | null
  paymentTerms: 'net_15' | 'net_30' | 'net_45' | 'net_60'
  currency: string
  notes: string | null
}

interface Tenant {
  id: string
  name: string
  slug: string
  status: string
}

interface ClientFormProps {
  client: Client | null
  onSave: (data: ClientFormData) => void
  onClose: () => void
}

export default function ClientForm({ client, onSave, onClose }: ClientFormProps) {
  // Mode selector for new clients: 'standalone' or 'from_tenant'
  const [creationMode, setCreationMode] = useState<'standalone' | 'from_tenant' | null>(client ? null : null)

  const [formData, setFormData] = useState({
    name: client?.name || '',
    slug: client?.slug || '',
    status: client?.status || 'prospect',
    clientType: client?.clientType || 'professional',
    linkedTenantId: client?.linkedTenantId || '',
    ownerUserId: client?.ownerUserId || '',
    contactName: client?.contactName || '',
    contactEmail: client?.contactEmail || '',
    contactPhone: client?.contactPhone || '',
    legalName: client?.legalName || '',
    taxId: client?.taxId || '',
    billingEmails: client?.billingEmails?.join(', ') || '',
    address: client?.address || '',
    paymentTerms: client?.paymentTerms || 'net_30',
    currency: client?.currency || 'USD',
    notes: client?.notes || ''
  })

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loadingTenants, setLoadingTenants] = useState(false)
  const [teamUsers, setTeamUsers] = useState<Array<{ id: string; name: string | null; email: string }>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load team users (for owner dropdown) once on mount. The dropdown
  // is useful on both create and edit so we don't gate on `!client`.
  useEffect(() => {
    api.listTeamUsers().then(res => {
      if (res.success && res.data) {
        setTeamUsers((res.data as { users: typeof teamUsers }).users || [])
      }
    }).catch(() => { /* non-fatal */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load tenants on mount for "create from tenant" option
  useEffect(() => {
    if (!client) {
      fetchTenants()
    }
  }, [])

  // Also load tenants when switching to hybrid
  useEffect(() => {
    if (formData.clientType === 'hybrid' && tenants.length === 0) {
      fetchTenants()
    }
  }, [formData.clientType])

  const fetchTenants = async () => {
    setLoadingTenants(true)
    try {
      const response = await api.listTenants()
      if (response.success && response.data?.tenants) {
        setTenants(response.data.tenants)
      }
    } catch (err) {
      console.error('Error fetching tenants:', err)
    } finally {
      setLoadingTenants(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Auto-generate slug from name
    if (name === 'name' && !client) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setFormData(prev => ({ ...prev, slug }))
    }

    // Clear linkedTenantId when changing to professional
    if (name === 'clientType' && value === 'professional') {
      setFormData(prev => ({ ...prev, linkedTenantId: '' }))
    }
  }

  // Handle selecting a tenant to create from
  const handleSelectTenantForCreation = (tenant: Tenant) => {
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      status: 'active',
      clientType: 'hybrid',
      linkedTenantId: tenant.id,
      ownerUserId: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      legalName: '',
      taxId: '',
      billingEmails: '',
      address: '',
      paymentTerms: 'net_30',
      currency: 'USD',
      notes: `Cliente creado desde tenant: ${tenant.name}`
    })
    setCreationMode('from_tenant')
  }

  // Mode selection screen for new clients
  if (!client && creationMode === null) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl max-w-2xl w-full border border-gray-700">
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h2 className="text-xl font-bold text-white">
              Nuevo Cliente de Servicios Profesionales
            </h2>
            <button
              onClick={onClose}
              className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-gray-300">
              ¿Como deseas crear el cliente?
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Option 1: Standalone */}
              <button
                onClick={() => setCreationMode('standalone')}
                className="p-6 rounded-xl border-2 border-gray-700 hover:border-accent transition-colors text-left group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-accent">
                    Crear desde Cero
                  </h3>
                </div>
                <p className="text-gray-300 text-sm">
                  Cliente nuevo que solo contratara servicios profesionales (ej: Marmate + PCI)
                </p>
              </button>

              {/* Option 2: From Tenant */}
              <div className="p-6 rounded-xl border-2 border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Desde Tenant Existente
                  </h3>
                </div>
                <p className="text-gray-300 text-sm mb-3">
                  Vincular a un cliente de Seguridad Gestionada (ej: iCash + WAF)
                </p>

                {loadingTenants ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                    Cargando tenants...
                  </div>
                ) : tenants.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No hay tenants disponibles
                  </p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {tenants.map(tenant => (
                      <button
                        key={tenant.id}
                        onClick={() => handleSelectTenantForCreation(tenant)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-accent/10 text-white hover:text-accent transition-colors text-sm flex items-center justify-between"
                      >
                        <span>{tenant.name}</span>
                        <span className="text-gray-500 text-xs">
                          {tenant.slug}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaving(true)

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('El nombre es requerido')
      }
      if (!formData.slug.trim()) {
        throw new Error('El slug es requerido')
      }
      if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        throw new Error('El slug solo puede contener letras minusculas, numeros y guiones')
      }
      if (formData.clientType === 'hybrid' && !formData.linkedTenantId) {
        throw new Error('Debes seleccionar un tenant para clientes hibridos')
      }

      // Validate email format if provided
      if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
        throw new Error('El email de contacto no es valido')
      }

      // Parse billing emails
      const billingEmails = formData.billingEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0)

      // Validate billing emails format
      for (const email of billingEmails) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          throw new Error(`Email de facturacion no valido: ${email}`)
        }
      }

      // Prepare data
      const clientData: ClientFormData = {
        name: formData.name,
        slug: formData.slug,
        status: formData.status as Client['status'],
        clientType: formData.clientType as Client['clientType'],
        linkedTenantId: formData.clientType === 'hybrid' ? formData.linkedTenantId : null,
        ownerUserId: formData.ownerUserId || null,
        contactName: formData.contactName || null,
        contactEmail: formData.contactEmail || null,
        contactPhone: formData.contactPhone || null,
        legalName: formData.legalName || null,
        taxId: formData.taxId || null,
        billingEmails,
        address: formData.address || null,
        paymentTerms: formData.paymentTerms as Client['paymentTerms'],
        currency: formData.currency,
        notes: formData.notes || null
      }

      await onSave(clientData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {client ? 'Editar Cliente' : 'Nuevo Cliente de Servicios Profesionales'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6" noValidate>
          {error && (
            <div className="bg-severity-critical/20 border border-severity-critical text-severity-critical px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Informacion Basica</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Ej: Acme Corp"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Slug (identificador) *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
                  placeholder="acme-corp"
                  pattern="[a-z0-9-]+"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Estado
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="prospect">Prospecto</option>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tipo de Cliente
                </label>
                <select
                  name="clientType"
                  value={formData.clientType}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="professional">Solo Profesional</option>
                  <option value="hybrid">Hibrido (con Tenant)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Moneda
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="USD">USD - Dolar</option>
                  <option value="CLP">CLP - Peso Chileno</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
            </div>

            {/* Tenant Linking (only for hybrid) */}
            {formData.clientType === 'hybrid' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Vincular a Tenant *
                </label>
                {loadingTenants ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                    Cargando tenants...
                  </div>
                ) : (
                  <select
                    name="linkedTenantId"
                    value={formData.linkedTenantId}
                    onChange={handleChange}
                    className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="">Seleccionar tenant...</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.slug})
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  El cliente hibrido tiene seguridad gestionada (tenant) + servicios profesionales
                </p>
                {/* Make the auto-billing behaviour explicit so the operator
                    doesn't wonder whether they need to create a recurring
                    service by hand. */}
                {formData.linkedTenantId && (
                  <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-xs text-green-200 flex items-start gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <strong>Facturación recurrente automática activa.</strong>{' '}
                      El tenant vinculado genera un snapshot cada día 15 con el
                      conteo de usuarios de JumpCloud × precio por plan. No necesitás
                      crear un servicio mensual manualmente — acá solo cargás los
                      servicios profesionales extra (consultorías, implementaciones,
                      proyectos one-time).
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Owner (Jarix team) — who runs point on this client */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Responsable interno (Owner)
            </label>
            <select
              name="ownerUserId"
              value={formData.ownerUserId}
              onChange={handleChange}
              className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">— Sin asignar —</option>
              {teamUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name ? `${u.name} (${u.email})` : u.email}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              Persona de Jarix que lleva la relación comercial / técnica del cliente.
              Útil para filtrar la lista y escalar follow-ups.
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Contacto</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nombre de Contacto
                </label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Juan Perez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email de Contacto
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="juan@empresa.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Telefono
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="+56 9 1234 5678"
                />
              </div>
            </div>
          </div>

          {/* Billing Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Facturacion</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Razon Social
                </label>
                <input
                  type="text"
                  name="legalName"
                  value={formData.legalName}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Acme Corp SpA"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  RUT / Tax ID
                </label>
                <input
                  type="text"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent font-mono"
                  placeholder="76.123.456-7"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Emails de Facturacion
                </label>
                <input
                  type="text"
                  name="billingEmails"
                  value={formData.billingEmails}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="facturacion@empresa.com, contabilidad@empresa.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separados por coma
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Terminos de Pago
                </label>
                <select
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="net_15">Net 15 (15 dias)</option>
                  <option value="net_30">Net 30 (30 dias)</option>
                  <option value="net_45">Net 45 (45 dias)</option>
                  <option value="net_60">Net 60 (60 dias)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Direccion
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Av. Providencia 1234, Santiago, Chile"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Notas</h3>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              placeholder="Notas internas sobre el cliente..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {client ? 'Guardar Cambios' : 'Crear Cliente'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
